import { gql } from '@moonpig/web-core-graphql'
import GoogleLogo from './assets/GoogleLogo.svg'
import AppleLogo from './assets/AppleLogo.svg'

export const PROVIDER_LOGOS = {
  google: GoogleLogo,
  apple: AppleLogo,
  mock: undefined,
}

export type ProviderType = keyof typeof PROVIDER_LOGOS

export const oidcErrorLogIdentifier = 'Oidc Error: '

export enum AccountLookupResponse {
  ACCOUNT_EXISTS_WITH_PASSWORD = 'ACCOUNT_EXISTS_WITH_PASSWORD',
  ACCOUNT_EXISTS_WITHOUT_PASSWORD = 'ACCOUNT_EXISTS_WITHOUT_PASSWORD',
  ACCOUNT_NOT_EXIST = 'ACCOUNT_NOT_EXIST',
  ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED',
  ERROR = 'ERROR',
}

export interface AccountLookupApiResponse {
  password: boolean
  state?: string
}

export interface AccountLookupResult {
  response: AccountLookupResponse
  errorMessage?: string
}

export type ValidationError = {
  field: string
  message: string
}

export const WizardForgottenPasswordGQL = gql`
  mutation WizardForgottenPassword($input: ForgottenPasswordInput!) {
    forgottenPassword(input: $input) {
      ... on ForgottenPasswordSent {
        __typename
        message
      }
      ... on ForgottenPasswordInvalidEmail {
        __typename
        message
      }
    }
  }
`

export interface AuthenticationFormInput {
  email: string
  password: string
  firstName: string
  lastName: string
  termsAccepted: boolean
  formStep: FormStep
  flowId: string
  loginCode: string
}

export enum FormStep {
  WELCOME = 'WELCOME',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  ACCOUNT_LINKING = 'ACCOUNT_LINKING',
  CODE_LOGIN = 'CODE_LOGIN',
  CODE_RECOVERY = 'CODE_RECOVERY',
  LOADING = 'LOADING',
}

export type PasswordRuleItem = {
  isValid: boolean
  text: string
}

export type AccountLookupInput = {
  email: string
}

export type LoginInput = {
  email: string
  password: string
  flowId?: string
}

export type RegisterInput = {
  email: string
  password: string
  firstName: string
  lastName: string
  termsAccepted: boolean
  source?: string
}

export type ValidateEmailInput = {
  email: string
}

export enum ValidateEmailResponse {
  ERROR,
  EMAIL_VALID,
  EMAIL_INVALID,
}

export type ValidateEmailResult = {
  response: ValidateEmailResponse
  errorMessage?: string
}

export type OidcProvider = {
  name: string
  id: ProviderType
  hasPrivateEmails: boolean
}

export type OidcProvidersResult = {
  providers: OidcProvider[]
}

export type OidcStartInput = {
  provider: string
  redirectUrl: string
}

export type OidcLoginInput = {
  upstreamParameters: Record<string, string>
  provider?: string
}

export enum OidcLoginStatus {
  ERROR,
  AUTHENTICATED,
  CREATED,
  LINKING_REQUIRED,
}

export type OidcLoginResult = {
  status:
    | OidcLoginStatus.ERROR
    | OidcLoginStatus.AUTHENTICATED
    | OidcLoginStatus.CREATED
}

export type OidcLoginAccountLinkingRequiredResult = {
  email: string
  flowId: string
  providerName: string
  methods: string[]
  providers: string[]
  status: OidcLoginStatus.LINKING_REQUIRED
}

export interface OidcAccountLinkingRequiredType {
  error: Error
  data: AccountLinkingRequiredData
}

export interface AccountLinkingRequiredData {
  email: string
  flowId: string
  providers: string[]
  providerName: string
  methods: string[]
}

export interface Error {
  code: string
}

export type OidcStartResult = {
  nonce: string
  flowId: string
  redirectBrowserTo: string
}

export type MagicLinkLoginResult =
  | {
      success: true
      skipped: boolean
    }
  | {
      success: false
      errorMessage?: string
    }

export type SendLoginCodeInput = {
  email: string
  flowId?: string
}

export type SendLoginCodeSuccessResult = {
  flowId: string
}

export type SendLoginCodeErrorResult = {
  errorCode?: CodeGenerateError
  errorMessage: string
}

export type SendLoginCodeResult =
  | SendLoginCodeSuccessResult
  | SendLoginCodeErrorResult

export type LoginWithCodeInput = {
  flowId: string
  email: string
  code: string
}

export type PasswordSetResult =
  | {
      success: true
    }
  | {
      success: false
      errorMessage?: string
    }

export type AuthenticationServiceType = {
  accountLookup(input: AccountLookupInput): Promise<AccountLookupResult>
  login(input: LoginInput): Promise<LoginResult>
  register(input: RegisterInput): Promise<RegisterResult>
  validateEmail(input: ValidateEmailInput): Promise<ValidateEmailResult>
  magicLinkLogin(token: string): Promise<MagicLinkLoginResult>
  sendLoginCode(input: SendLoginCodeInput): Promise<SendLoginCodeResult>
  loginWithCode(input: LoginWithCodeInput): Promise<LoginResult>
  passwordSet(newPassword: string): Promise<PasswordSetResult>
}

export type AuthenticationOidcServiceType = {
  getOidcProviders(): Promise<OidcProvidersResult>
  start(input: OidcStartInput): Promise<OidcStartResult | null>
  login(
    input: OidcLoginInput,
  ): Promise<OidcLoginResult | OidcLoginAccountLinkingRequiredResult>
}

export enum LoginErrorCodes {
  IDENTITY_INACTIVE = 'IDENTITY_INACTIVE',
}

export interface LoginResult {
  success: boolean
  accountLocked?: boolean
  errorTitle?: string
  errorMessage?: string
  errorCode?: string
}

export interface RegisterResult {
  success: boolean
  errorMessage?: string
  errors?: Array<{
    field: string
    message: string
  }>
}

export enum CodeGenerateError {
  FLOW_EXPIRED = 'FLOW_EXPIRED',
  CUSTOMER_NOT_FOUND = 'CUSTOMER_NOT_FOUND',
}
